import type { AppLocale, MessageTree } from "@airange/shared/i18n-common";

export const redblueDirectorPageMessages: Record<AppLocale, MessageTree> = {
  "zh-CN": {
    redblue: {
      pages: {
        director: {
          subtitle: "导调台必须绑定具体演练活动后使用。",
          activitySubtitle: "演练活动 #{activityCode}",
          selectorNotice:
            "导调台必须先绑定具体演练活动；当前列表用于选择待导调的活动，任务、公告和过程记录都归属于活动内部。",
          alertTitle: "导调台只操作活动内任务、公告和过程记录；任务仍归属当前演练活动。",
          readonlyTitle: "该活动当前不在进行中，导调台不开放操作。",
          readonlyDescription:
            "导调台只对进行中活动开放；当前不允许下发任务、发布公告或发起扣分。如需继续处理，请返回活动详情查看当前状态或改走仍可用的上下文入口。",
          archivedReadonlyTitle: "该活动已归档，导调台只保留历史只读查看。",
          archivedReadonlyDescription:
            "归档活动不再允许下发任务、发布公告或发起扣分；如需审计，请改走态势与复盘或查看现有历史记录。",
          tabs: {
            tasks: "任务下发",
            announcements: "公告通知",
            events: "过程记录",
          },
          actions: {
            publishNotice: "发布公告",
            dispatchSelectedTasks: "下发所选任务",
          },
          confirm: {
            dispatchSelectedTasks: "确认下发 {count} 个任务？",
            dispatchSelectedTasksOk: "确认下发",
          },
          success: {
            tasksDispatched: "任务已下发",
            noticePublished: "公告已发布",
            penaltySubmitted: "扣分已提交",
          },
          noticeForm: {
            title: "发布导调公告",
            titleLabel: "公告标题",
            titleRequired: "请输入公告标题",
            targetTypeLabel: "通知目标",
            targetTypeRequired: "请选择通知目标",
            targetRefLabel: "目标引用",
            targetRefPlaceholder: "全体公告可留空；阵营/席位/角色填写对应编码",
            contentLabel: "公告内容",
            contentRequired: "请输入公告内容",
          },
          penaltyForm: {
            title: "发起导调扣分",
            taskCodeLabel: "关联任务编码",
            campCodeLabel: "关联阵营编码",
            seatCodeLabel: "关联席位编码",
            userIdLabel: "关联用户 ID",
            scoreDeltaLabel: "分值变化",
            scoreDeltaRequired: "请输入扣分值",
            reasonLabel: "扣分原因",
            reasonRequired: "请输入扣分原因",
          },
        },
      },
    },
  },
  en: {
    redblue: {
      pages: {
        director: {
          subtitle: "Director workspace requires a bound activity context.",
          activitySubtitle: "Activity #{activityCode}",
          selectorNotice:
            "Director workspace must bind to an activity first. Use this list to choose the target activity; tasks, announcements, and events stay inside the activity context.",
          alertTitle:
            "Director workspace only operates on in-activity tasks, announcements, and event records.",
          readonlyTitle:
            "This activity is not running, so the director workspace is not open for operations.",
          readonlyDescription:
            "Director operations are only available while an activity is running. Task dispatch, announcements, and penalties are now disabled here; return to activity detail or use any still-available context entry.",
          archivedReadonlyTitle:
            "This activity is archived, so the director workspace is now read-only history.",
          archivedReadonlyDescription:
            "Archived activities no longer allow task dispatch, announcements, or penalties. Use review or existing history records for audit.",
          tabs: {
            tasks: "Task Dispatch",
            announcements: "Announcements",
            events: "Event Log",
          },
          actions: {
            publishNotice: "Publish Announcement",
            dispatchSelectedTasks: "Dispatch Selected Tasks",
          },
          confirm: {
            dispatchSelectedTasks: "Dispatch {count} selected tasks?",
            dispatchSelectedTasksOk: "Dispatch",
          },
          success: {
            tasksDispatched: "Tasks dispatched",
            noticePublished: "Announcement published",
            penaltySubmitted: "Penalty submitted",
          },
          noticeForm: {
            title: "Publish Director Announcement",
            titleLabel: "Announcement Title",
            titleRequired: "Enter an announcement title",
            targetTypeLabel: "Target Type",
            targetTypeRequired: "Select a target type",
            targetRefLabel: "Target Reference",
            targetRefPlaceholder:
              "Leave empty for all. Use the matching code for camp, seat, or role targets.",
            contentLabel: "Announcement Content",
            contentRequired: "Enter announcement content",
          },
          penaltyForm: {
            title: "Create Director Penalty",
            taskCodeLabel: "Related Task Code",
            campCodeLabel: "Related Camp Code",
            seatCodeLabel: "Related Seat Code",
            userIdLabel: "Related User ID",
            scoreDeltaLabel: "Score Delta",
            scoreDeltaRequired: "Enter a penalty score delta",
            reasonLabel: "Penalty Reason",
            reasonRequired: "Enter a penalty reason",
          },
        },
      },
    },
  },
  ja: {
    redblue: {
      pages: {
        director: {
          subtitle: "ディレクター画面は特定の演習活動に紐付けて使用します。",
          activitySubtitle: "演習活動 #{activityCode}",
          selectorNotice:
            "ディレクター画面は先に演習活動へ紐付ける必要があります。この一覧から対象活動を選び、タスク、告知、イベント記録はすべて活動内部で扱います。",
          alertTitle: "ディレクター画面は活動内のタスク、告知、イベント記録のみを操作します。",
          readonlyTitle: "この活動は進行中ではないため、ディレクター画面で操作できません。",
          readonlyDescription:
            "ディレクター操作は活動が進行中の間だけ利用できます。現在はタスク配布、告知公開、減点作成はできません。活動詳細へ戻るか、利用可能な別コンテキストを使用してください。",
          archivedReadonlyTitle:
            "この活動はアーカイブ済みのため、ディレクター画面は履歴の閲覧専用です。",
          archivedReadonlyDescription:
            "アーカイブ済み活動ではタスク配布、告知公開、減点作成はできません。監査が必要な場合はレビュー画面または既存の履歴を参照してください。",
          tabs: {
            tasks: "タスク配布",
            announcements: "告知",
            events: "イベント記録",
          },
          actions: {
            publishNotice: "告知を公開",
            dispatchSelectedTasks: "選択タスクを配布",
          },
          confirm: {
            dispatchSelectedTasks: "{count} 件のタスクを配布しますか？",
            dispatchSelectedTasksOk: "配布",
          },
          success: {
            tasksDispatched: "タスクを配布しました",
            noticePublished: "告知を公開しました",
            penaltySubmitted: "減点を送信しました",
          },
          noticeForm: {
            title: "ディレクター告知を公開",
            titleLabel: "告知タイトル",
            titleRequired: "告知タイトルを入力してください",
            targetTypeLabel: "通知対象",
            targetTypeRequired: "通知対象を選択してください",
            targetRefLabel: "対象参照",
            targetRefPlaceholder:
              "全体通知は空欄のままで構いません。陣営、席位、メンバー、ロールの場合は対応コードを入力してください。",
            contentLabel: "告知内容",
            contentRequired: "告知内容を入力してください",
          },
          penaltyForm: {
            title: "ディレクター減点を作成",
            taskCodeLabel: "関連タスクコード",
            campCodeLabel: "関連陣営コード",
            seatCodeLabel: "関連席位コード",
            userIdLabel: "関連ユーザー ID",
            scoreDeltaLabel: "点数差分",
            scoreDeltaRequired: "減点値を入力してください",
            reasonLabel: "減点理由",
            reasonRequired: "減点理由を入力してください",
          },
        },
      },
    },
  },
};
